import styles from './styles.module.scss';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xLg';

const DEFAULT_SIZE: IconSize = 'md';

const SIZE_MAP: Record<IconSize, number> = {
  xs: 16,
  sm: 20,
  md: 24,
  lg: 32,
  xLg: 48,
};

type IconAriaAttributes = Pick<React.AriaAttributes, 'aria-label' | 'aria-hidden'>;

type Props = {
  size?: IconSize;
  strokeWidth?: number;
  className?: string;
} & IconAriaAttributes;

/**
 * `*.svg?react` で import した SVG React コンポーネントを受け取り、
 * `size` / `aria-label` / `aria-hidden` の I/F を持つアイコンコンポーネントを生成する。
 *
 * **サイズと stroke-width:**
 * `SIZE_MAP` で width/height を、`strokeWidth` prop で stroke-width を設定する（デフォルト: 2）。
 * Lucide SVG は root の `<svg>` にのみ `stroke-width` 属性を持ち子要素は継承する構造のため、
 * props として `strokeWidth` を渡すだけで全要素に反映される。
 * アイコンごとにデザインが異なる場合は呼び出し側で上書きできる。
 *
 * **a11y:**
 * `aria-label` を渡した場合は `role="img"` を付与する。
 *
 * **スタイル:**
 * `styles.svgIcon` を SVG に直接適用することで、legacy の `<span>` ラッパーが担っていた
 * `color: text('High')` を引き継ぐ。SVG が `stroke="currentColor"` を使っている場合に、
 * 親の `color` に依存しないストローク色を保証する。
 * `className` を渡すと `styles.svgIcon` に追加でクラスが付与される。
 *
 * @param Svg - `*.svg?react` import で取得した SVG React コンポーネント（SVGProps を受け取る FC）
 */
export const createSvgIcon = (Svg: React.FC<React.SVGProps<SVGSVGElement>>) => {
  const Icon: React.FC<Props> = ({
    size = DEFAULT_SIZE,
    strokeWidth = 2,
    className,
    'aria-label': ariaLabel,
    'aria-hidden': ariaHidden,
  }) => (
    <Svg
      className={className ? `${styles.svgIcon} ${className}` : styles.svgIcon}
      width={SIZE_MAP[size]}
      height={SIZE_MAP[size]}
      strokeWidth={strokeWidth}
      role={ariaLabel ? 'img' : undefined}
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
    />
  );

  return Icon;
};
